import type {
    ComplianceModuleKey,
    ComplianceRequirementListView,
    ComplianceRequirementView,
    SubmitComplianceEvidenceResponse
} from "@solomon-apps/shared";

import { orgApiFetch } from "./orgApiFetch";

const BASE = "/api/org/client/compliance";

const EMPTY_REQUIREMENTS: ComplianceRequirementListView = {
    requirements: [],
    summary: {
        requirementCount: 0,
        satisfiedCount: 0,
        missingCount: 0,
        pendingReviewCount: 0,
        blocksClientWrites: false,
        blockedRequirementUuids: [],
        warningRequirementUuids: []
    }
};

function isComplianceRequirementView(value: unknown): value is ComplianceRequirementView {
    if (!value || typeof value !== "object") {
        return false;
    }

    const requirement = value as Partial<ComplianceRequirementView>;
    return (
        typeof requirement.uuid === "string" &&
        typeof requirement.moduleKey === "string" &&
        typeof requirement.module === "object" &&
        requirement.module !== null &&
        typeof requirement.satisfied === "boolean" &&
        typeof requirement.blocksClientWrites === "boolean" &&
        typeof requirement.warningOnly === "boolean" &&
        Array.isArray(requirement.pendingReviews)
    );
}

export function normalizeComplianceRequirementList(
    input: Partial<ComplianceRequirementListView> | null | undefined
): ComplianceRequirementListView {
    const requirements = Array.isArray(input?.requirements)
        ? input.requirements.filter(isComplianceRequirementView)
        : [];
    const summary = input?.summary;

    return {
        requirements,
        summary: {
            requirementCount:
                typeof summary?.requirementCount === "number" ? summary.requirementCount : requirements.length,
            satisfiedCount:
                typeof summary?.satisfiedCount === "number"
                    ? summary.satisfiedCount
                    : requirements.filter((requirement) => requirement.satisfied).length,
            missingCount:
                typeof summary?.missingCount === "number"
                    ? summary.missingCount
                    : requirements.filter((requirement) => !requirement.satisfied).length,
            pendingReviewCount:
                typeof summary?.pendingReviewCount === "number"
                    ? summary.pendingReviewCount
                    : requirements.reduce((count, requirement) => count + requirement.pendingReviews.length, 0),
            blocksClientWrites:
                typeof summary?.blocksClientWrites === "boolean"
                    ? summary.blocksClientWrites
                    : requirements.some((requirement) => requirement.blocksClientWrites),
            blockedRequirementUuids: Array.isArray(summary?.blockedRequirementUuids)
                ? summary.blockedRequirementUuids.filter((uuid): uuid is string => typeof uuid === "string")
                : requirements
                      .filter((requirement) => requirement.blocksClientWrites)
                      .map((requirement) => requirement.uuid),
            warningRequirementUuids: Array.isArray(summary?.warningRequirementUuids)
                ? summary.warningRequirementUuids.filter((uuid): uuid is string => typeof uuid === "string")
                : requirements.filter((requirement) => requirement.warningOnly).map((requirement) => requirement.uuid)
        }
    };
}

export async function fetchClientComplianceRequirements(): Promise<ComplianceRequirementListView> {
    const response = await orgApiFetch<Partial<ComplianceRequirementListView>>(`${BASE}/requirements`);
    return normalizeComplianceRequirementList(response);
}

export async function submitClientComplianceEvidence(input: {
    moduleKey: ComplianceModuleKey;
    payload?: Record<string, unknown> | null;
}): Promise<SubmitComplianceEvidenceResponse> {
    return orgApiFetch<SubmitComplianceEvidenceResponse>(`${BASE}/evidence`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            moduleKey: input.moduleKey,
            payload: input.payload ?? null
        })
    });
}

export { EMPTY_REQUIREMENTS };
